import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import contentData from "../../data/content/education/educationContent.json";
import medicalSenseiContent from "../../data/content/education/medicalSenseiContent.json";
import { DataKeys, DataStructure } from "../../types";
import { useDocumentTitle } from "../../functions/useDocumentTitle";
import { useLang } from "../Context";
import { NavTree } from "../navigation/NavLinkPages";
import NoContent from "../content/NoContent";
import { SlideInAnim } from "../tools/Animations";
import { HealthFaqSection } from "../health/HealthFaqSection";
import { EducationBlendVisual } from "../education/EducationBlendVisual";
import { HealthSection } from "../health/HealthSection";
import { getLocalized, HealthLang, LocalizedString } from "../health/healthTypes";
import {
    healthAimmaDoctorHeroSurface,
    healthAimmaDoctorSectionContentOffset,
    healthAimmaDoctorHeroCopyForeground,
    healthChildPageHeroCopyStack,
    healthChildPageHeroGrid,
    healthChildPageHeroImageColumn,
    healthChildPageHeroProductLineRow,
    healthChildPageHeroSectionY,
    healthCol,
    healthContentPadX,
    healthSectionInnerStyle,
    healthSectionOuter,
} from "../health/healthLayout";
import {
    HEALTH_CTA_BAND_BG,
    healthAimmaDoctorHeroBottomFade,
    healthBody,
    healthChildPageLead,
    healthChildPageTitle,
    healthCtaBand,
    healthCtaButton,
    healthCtaSecondaryButton,
    healthCtaSubtitle,
    healthCtaTitle,
    healthFocusVisible,
    healthPrimaryButton,
    healthProductLineLabel,
    healthSecondaryButton,
    healthButtonArrowFa,
    healthButtonLabel,
} from "../health/healthVisualStyles";
import { scrollToHealthHashAnchor } from "../health/healthScroll";
import { EducationFrameworkSection } from "../education/EducationFrameworkSection";
import { EducationHero } from "../education/EducationHero";
import { EducationIntroSection } from "../education/EducationIntroSection";
import { EducationLearningLoopSection } from "../education/EducationLearningLoopSection";
import { EducationHealthBridgeSection } from "../education/EducationHealthBridgeSection";
import { EducationInstitutionSection } from "../education/EducationInstitutionSection";
import { EducationWhyMedicalSection } from "../education/EducationWhyMedicalSection";
import { EducationModulesSection } from "../education/EducationModulesSection";
import { EducationPrimaryCta } from "../education/EducationPrimaryCta";
import { EducationProblemSection } from "../education/EducationProblemSection";
import { EducationProductSuiteSection } from "../education/EducationProductSuiteSection";
import { EducationSolutionSection } from "../education/EducationSolutionSection";
import { educationLocalePath } from "../education/educationLocalePath";
import { SpacingFrame } from "../display/Frame";

const typedContent: DataStructure = contentData;
const medicalData = medicalSenseiContent as DataKeys;

function localePath(path: string, lang: HealthLang): string {
    return educationLocalePath(path, lang);
}

export default function Education() {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const data = typedContent["Education"];

    useDocumentTitle(getLocalized(data.pageTitle, hLang));

    return (
        <main className="overflow-x-clip bg-white text-left">
            <EducationHero data={data.hero} />
            <NavTree />
            <EducationIntroSection data={data.intro} />
            <EducationProblemSection data={data.problem} />
            <EducationSolutionSection data={data.solution} />
            <EducationFrameworkSection data={data.learningFramework} />
            <EducationProductSuiteSection data={data.productSuite} />
            <HealthFaqSection data={data.faq} />
            <EducationPrimaryCta data={data.ctaPrimary} />
        </main>
    );
}

export function MedicalSenseiPage() {
    const { lang } = useLang();
    const navigate = useNavigate();
    const hLang = lang as HealthLang;

    useDocumentTitle(getLocalized(medicalData.pageTitle as LocalizedString, hLang));

    const hero = medicalData.hero as DataKeys;
    const whyMedical = medicalData.whyMedical as DataKeys;
    const learningJourney = medicalData.learningJourney as DataKeys;
    const modules = medicalData.modules as DataKeys;
    const programValue = medicalData.programValue as DataKeys;
    const healthBridge = medicalData.healthBridge as DataKeys;
    const faq = medicalData.faq as DataKeys;
    const cta = medicalData.cta as DataKeys;

    const heroCtaPrimary = hero.ctaPrimary as DataKeys;
    const heroCtaSecondary = hero.ctaSecondary as DataKeys;

    return (
        <main className="overflow-x-clip bg-white text-left">
            <header
                className={`${healthSectionOuter} ${healthAimmaDoctorHeroSurface}`}
            >
                <div className={healthAimmaDoctorHeroBottomFade} aria-hidden="true" />
                <div className="relative m-auto w-full" style={healthSectionInnerStyle}>
                    <div className={`${healthContentPadX} ${healthChildPageHeroSectionY}`}>
                        <div className={healthChildPageHeroGrid}>
                            <p
                                className={`${healthProductLineLabel} ${healthChildPageHeroProductLineRow} ${healthAimmaDoctorHeroCopyForeground}`}
                            >
                                {getLocalized(hero.productLineLabel, hLang)}
                            </p>
                            <div
                                className={`${healthCol} ${healthChildPageHeroCopyStack} ${healthAimmaDoctorHeroCopyForeground}`}
                            >
                                <h1 className={healthChildPageTitle}>
                                    {getLocalized(hero.title, hLang)}
                                </h1>
                                <p className={healthChildPageLead}>
                                    {getLocalized(hero.subtitle, hLang)}
                                </p>
                                {hero.leadSecondary && (
                                    <p className={`${healthBody} text-base text-grey-600`}>
                                        {getLocalized(hero.leadSecondary, hLang)}
                                    </p>
                                )}
                                <div className="flex w-full flex-col gap-3 pt-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-4">
                                    <a
                                        href={(heroCtaPrimary?.href as string) ?? "#learning-journey"}
                                        onClick={(event) => {
                                            scrollToHealthHashAnchor(
                                                (heroCtaPrimary?.href as string) ??
                                                    "#learning-journey",
                                                event,
                                                true,
                                                (to, options) => navigate(to, options)
                                            );
                                        }}
                                        className={`${healthPrimaryButton} ${healthFocusVisible}`}
                                    >
                                        {getLocalized(heroCtaPrimary?.label, hLang)}
                                    </a>
                                    <a
                                        href={(heroCtaSecondary?.href as string) ?? "#modules"}
                                        onClick={(event) => {
                                            scrollToHealthHashAnchor(
                                                (heroCtaSecondary?.href as string) ??
                                                    "#modules",
                                                event,
                                                true,
                                                (to, options) => navigate(to, options)
                                            );
                                        }}
                                        className={`${healthSecondaryButton} ${healthFocusVisible}`}
                                    >
                                        <span className={healthButtonLabel}>
                                            {getLocalized(heroCtaSecondary?.label, hLang)}
                                        </span>
                                        <FontAwesomeIcon icon={faArrowRight} className={healthButtonArrowFa} />
                                    </a>
                                </div>
                            </div>

                            <SlideInAnim direction="right" className="relative z-0">
                                <div className={healthChildPageHeroImageColumn}>
                                    <EducationBlendVisual
                                        assetKey={
                                            (hero.visual as DataKeys)?.assetKey as
                                                | string
                                                | undefined
                                        }
                                        alt={getLocalized(
                                            (hero.visual as DataKeys)?.alt as LocalizedString,
                                            hLang
                                        )}
                                        loading="eager"
                                    />
                                </div>
                            </SlideInAnim>
                        </div>
                    </div>
                </div>
            </header>
            <NavTree />

            <EducationWhyMedicalSection data={whyMedical} />

            <EducationLearningLoopSection data={learningJourney} />

            <EducationModulesSection data={modules} />

            <EducationInstitutionSection data={programValue} />

            <EducationHealthBridgeSection data={healthBridge} />

            <HealthFaqSection data={faq} />

            <HealthSection id="cta" compact style={{ backgroundColor: HEALTH_CTA_BAND_BG }}>
                <div className={healthCtaBand}>
                    <SpacingFrame spacing={32} center>
                        <div className="flex w-full min-w-0 flex-col gap-2 text-left lg:items-center lg:text-center">
                            <h2 className={healthCtaTitle}>
                                {getLocalized(cta.title as LocalizedString, hLang)}
                            </h2>
                            <p className={healthCtaSubtitle}>
                                {getLocalized(cta.subtitle as LocalizedString, hLang)}
                            </p>
                        </div>
                        <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:justify-center">
                            <Link
                                to={localePath((cta.href as string) ?? "/contact-us", hLang)}
                                className={`${healthCtaButton} ${healthFocusVisible}`}
                            >
                                {getLocalized(cta.ctaLabel as LocalizedString, hLang)}
                            </Link>
                            <Link
                                to={localePath(
                                    (cta.secondaryHref as string) ?? "/education",
                                    hLang
                                )}
                                className={`${healthCtaSecondaryButton} ${healthFocusVisible}`}
                            >
                                {getLocalized(cta.secondaryLabel as LocalizedString, hLang)}
                            </Link>
                        </div>
                    </SpacingFrame>
                </div>
            </HealthSection>
        </main>
    );
}

export function EducationSubpages() {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const { educationSolutions } = useParams();

    if (educationSolutions === "business-sensei") {
        const hubPath = localePath("/education#product-suite", hLang);
        const [path, hash] = hubPath.split("#");
        return <Navigate to={`${path}${hash ? `#${hash}` : ""}`} replace />;
    }

    if (educationSolutions === "medical-sensei") {
        return <MedicalSenseiPage />;
    }

    return <NoContent />;
}
